import React, { useState } from "react";
import {
  Checkbox,
  DataTable,
  DataTableColumn,
  FilterBar,
  FiltersContainer,
  SearchBar,
  Switch,
  Table,
  hasActiveFilters,
} from "@sscs/ui";
import { SharedReportLinkRecipient } from "../../../../../types";
import RecipientInfo from "./RecipientInfo";
import styles from "./recipientsTable.module.scss";
import { RecipientsTableFilter, filterRecipients, initFilter } from "./utils";

const getFilteredSelectedRecipientEmails = (
  allRecipients: SharedReportLinkRecipient[],
  selectedRecipientEmails: string[],
  filter: RecipientsTableFilter,
): string[] => {
  const newFilteredRecipientEmails = filterRecipients(
    allRecipients,
    filter,
  ).map((recipient) => recipient.email);
  return selectedRecipientEmails.filter((email) =>
    newFilteredRecipientEmails.includes(email),
  );
};

type RecipientRowProps = {
  recipient: SharedReportLinkRecipient;
  isSelected: boolean;
  onRecipientToggle: (email: string, isSelected: boolean) => void;
};

const RecipientRow = ({
  recipient,
  isSelected,
  onRecipientToggle,
}: RecipientRowProps): React.JSX.Element => {
  return (
    <Table.Row>
      <Table.Cell>
        <Checkbox
          checked={isSelected}
          onChange={(_, checked) => {
            onRecipientToggle(recipient.email, checked);
          }}
          slotProps={{ input: { "aria-label": `Select ${recipient.email}` } }}
        />
      </Table.Cell>
      <Table.Cell>
        <RecipientInfo recipient={recipient} />
      </Table.Cell>
    </Table.Row>
  );
};

type SelectableRecipientsTableProps = {
  recipients: SharedReportLinkRecipient[];
  selectedRecipientEmails: string[];
  onChangeRecipients: (recipients: string[]) => void;
};

const SelectableRecipientsTable = ({
  recipients,
  selectedRecipientEmails,
  onChangeRecipients,
}: SelectableRecipientsTableProps): React.JSX.Element => {
  const [filter, setFilter] = useState<RecipientsTableFilter>(initFilter);

  const filteredRecipients = filterRecipients(recipients, filter);

  const selectedFilteredCount = filteredRecipients.filter((recipient) =>
    selectedRecipientEmails.includes(recipient.email),
  ).length;

  const onSelectAllToggle = (checked: boolean) => {
    let newSelectedRecipients: Set<string> = new Set();

    if (checked) {
      newSelectedRecipients = new Set([
        ...selectedRecipientEmails,
        ...filteredRecipients.map((recipient) => recipient.email),
      ]);
    }

    onChangeRecipients(Array.from(newSelectedRecipients));
  };

  const columns: DataTableColumn[] = [
    {
      id: "select",
      width: "60px",
      header: (
        <Checkbox
          checked={
            filteredRecipients.length > 0 &&
            selectedFilteredCount === filteredRecipients.length
          }
          indeterminate={
            selectedFilteredCount > 0 &&
            selectedFilteredCount < filteredRecipients.length
          }
          disabled={filteredRecipients.length === 0}
          onChange={(_, checked) => {
            onSelectAllToggle(checked);
          }}
          slotProps={{ input: { "aria-label": "Select all" } }}
        />
      ),
    },
    { id: "recipient", label: "Recipient", width: "100%" },
  ];

  const onRecipientToggle = (email: string, isSelected: boolean) => {
    if (isSelected && !selectedRecipientEmails.includes(email)) {
      onChangeRecipients([...selectedRecipientEmails, email]);
    } else {
      onChangeRecipients(
        selectedRecipientEmails.filter(
          (recipientEmail) => recipientEmail !== email,
        ),
      );
    }
  };

  return (
    <div className={styles.container}>
      <FiltersContainer
        found={{
          id: "selectable-recipients-clear-filters",
          count: filteredRecipients.length,
          initialFilters: initFilter,
          activeFilters: filter,
          onClear: () => {
            setFilter(initFilter);
            onChangeRecipients(
              getFilteredSelectedRecipientEmails(
                recipients,
                selectedRecipientEmails,
                initFilter,
              ),
            );
          },
        }}
      >
        <FilterBar
          stacking="never"
          filters={[
            {
              id: "email",
              control: (
                <SearchBar
                  id="search-recipient-email"
                  value={filter.email ?? ""}
                  onChange={(value) => {
                    const newFilter = {
                      ...filter,
                      email: value,
                    };

                    setFilter(newFilter);

                    onChangeRecipients(
                      getFilteredSelectedRecipientEmails(
                        recipients,
                        selectedRecipientEmails,
                        newFilter,
                      ),
                    );
                  }}
                  variant="outlined"
                  label="Search for E-mail"
                  slotProps={{ htmlInput: { maxLength: 256 } }}
                  debounceTimer={250}
                />
              ),
            },
            {
              id: "not-viewed",
              fit: true,
              control: (
                <Switch
                  title="Filter recipients"
                  label="Not viewed"
                  checked={filter.not_viewed_report_only}
                  onChange={(event) => {
                    const newFilter = {
                      ...filter,
                      not_viewed_report_only: event.target.checked,
                    };

                    setFilter(newFilter);

                    onChangeRecipients(
                      getFilteredSelectedRecipientEmails(
                        recipients,
                        selectedRecipientEmails,
                        newFilter,
                      ),
                    );
                  }}
                  className={styles.label}
                />
              ),
            },
          ]}
        />
      </FiltersContainer>
      <div className={styles["table-scroll"]}>
        <DataTable
          chrome="plain"
          columns={columns}
          empty={{
            subject: "recipients",
            filters: {
              active: hasActiveFilters(initFilter, filter),
              onReset: () => setFilter(initFilter),
            },
          }}
        >
          {filteredRecipients.map((recipient) => (
            <RecipientRow
              key={recipient.email}
              recipient={recipient}
              isSelected={selectedRecipientEmails.includes(recipient.email)}
              onRecipientToggle={onRecipientToggle}
            />
          ))}
        </DataTable>
      </div>
    </div>
  );
};

export default SelectableRecipientsTable;
